import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from "react-router-dom";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import { Loader2, Search, SlidersHorizontal, PanelLeftClose, UserRound, Users, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SearchBar } from "@/components/search/SearchBar";
import type { LocationValue } from "@/components/location/LocationAutocompleteInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  FilterPanel,
  DEFAULT_FILTERS,
  SectionLabel,
  Divider,
  MultiSelectChips,
  type FiltersState,
} from "@/components/search/FilterPanel";
import { ActiveFilterChips, type Chip } from "@/components/search/ActiveFilterChips";
import { MoreFiltersDrawer } from "@/components/search/MoreFiltersDrawer";
import { useGeoDefaults } from "@/hooks/useGeoDefaults";
import { useTeachingModes } from "@/hooks/useValueCatalog";
import { useSubjectTopics } from "@/hooks/useSubjectTopics";
import { useAuth } from "@/hooks/use-auth-compat";
import { useOneToOneSearchInfinite, useGroupSearchInfinite, useCrashCourseSearchInfinite } from "@/hooks/use-offering-search";
import type {
  OfferingType,
  CommonOfferingSearchParams,
  GroupOfferingSearchParams,
  CrashCourseSearchParams,
} from "@/services/offeringSearchService";
import { OfferingCard } from "@/components/offerings/OfferingCard";
import { EmptyState } from "@/components/common/EmptyState";
import { ZeroResultsState } from "./components/ZeroResultsState";
import type { SearchContextSnapshot } from "@/types/searchResults";
import { saveSearch, clearSearch, decideSearchMemory } from "@/lib/offeringSearchMemory";

const PAGE_TITLES: Record<OfferingType, string> = {
  ONE_TO_ONE: "Find One-to-One Teachers",
  GROUP: "Find Group Classes",
  CRASH_COURSE: "Find Crash Courses",
};

const NOUN: Record<OfferingType, string> = {
  ONE_TO_ONE: "offerings",
  GROUP: "group classes",
  CRASH_COURSE: "crash courses",
};

const TYPE_CONFIG = {
  ONE_TO_ONE: {
    Icon: UserRound,
    badge: "One-to-One",
    badgeCls: "bg-indigo-100 text-indigo-700",
    iconCls: "text-indigo-600",
    emptyIconBg: "bg-indigo-50",
  },
  GROUP: {
    Icon: Users,
    badge: "Group Classes",
    badgeCls: "bg-violet-100 text-violet-700",
    iconCls: "text-violet-600",
    emptyIconBg: "bg-violet-50",
  },
  CRASH_COURSE: {
    Icon: Zap,
    badge: "Crash Courses",
    badgeCls: "bg-amber-100 text-amber-700",
    iconCls: "text-amber-600",
    emptyIconBg: "bg-amber-50",
  },
};

// Offering endpoints support POPULARITY (not the teacher-only EXPERIENCE).
const OFFERING_SORT_ITEMS = [
  { value: "RELEVANCE", label: "Relevance" },
  { value: "PRICE_LOW", label: "Price: Low to High" },
  { value: "PRICE_HIGH", label: "Price: High to Low" },
  { value: "POPULARITY", label: "Most popular" },
];

// "Soonest start" only makes sense for offerings that have a start date — GROUP
// (next batch start) and CRASH_COURSE (course start). ONE_TO_ONE has none.
const START_DATE_SORT_ITEMS = [...OFFERING_SORT_ITEMS, { value: "START_DATE", label: "Soonest start" }];

function filtersFromURL(sp: URLSearchParams): FiltersState {
  return {
    ...DEFAULT_FILTERS,
    sortBy: sp.get("sortBy") || "RELEVANCE",
    priceMin: sp.get("priceMin") ? Number(sp.get("priceMin")) : null,
    priceMax: sp.get("priceMax") ? Number(sp.get("priceMax")) : null,
    offeringType: "ALL", // fixed by the route on per-type pages
    gradeBand: sp.getAll("gradeBand"),
    language: sp.getAll("language"),
    curriculum: sp.getAll("curriculum"),
    availabilityTimeOfDay: sp.get("availabilityTimeOfDay") || "",
    trialSessionAvailable: sp.get("trialSessionAvailable") === "true" ? true : null,
    targetExamId: sp.get("targetExamId") || "",
    topicKeywords: sp.get("topicKeywords") || "",
  };
}

const TYPE_TABS: { type: OfferingType; label: string; path: string; Icon: React.ElementType; activeCls: string; hoverCls: string }[] = [
  {
    type: "ONE_TO_ONE",
    label: "One-to-One",
    path: "/search/one-to-one",
    Icon: UserRound,
    activeCls: "bg-indigo-600 text-white border-indigo-600 shadow-sm",
    hoverCls: "hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200",
  },
  {
    type: "GROUP",
    label: "Group Classes",
    path: "/search/group",
    Icon: Users,
    activeCls: "bg-violet-600 text-white border-violet-600 shadow-sm",
    hoverCls: "hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200",
  },
  {
    type: "CRASH_COURSE",
    label: "Crash Courses",
    path: "/search/crash-course",
    Icon: Zap,
    activeCls: "bg-amber-500 text-white border-amber-500 shadow-sm",
    hoverCls: "hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200",
  },
];

export default function OfferingSearchPage({
  offeringType,
  embedded = false,
}: {
  offeringType: OfferingType;
  embedded?: boolean;
}) {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const accessToken = (user?.access_token as string | undefined) || undefined;
  const sortItems = (offeringType === "GROUP" || offeringType === "CRASH_COURSE") ? START_DATE_SORT_ITEMS : OFFERING_SORT_ITEMS;
  const { data: geoDefaults } = useGeoDefaults(accessToken);
  const teachingModes = useTeachingModes();
  const [showFilters, setShowFilters] = useState(false);

  const filters = useMemo(() => filtersFromURL(sp), [sp]);
  const q = sp.get("q") ?? undefined;
  const subjectId = sp.get("subjectId") ?? undefined;

  // Location (from the homepage search bar)
  const latitude = sp.get("lat") ? Number(sp.get("lat")) : undefined;
  const longitude = sp.get("lng") ? Number(sp.get("lng")) : undefined;
  const hasLocation = latitude != null && longitude != null;
  const radiusKm = sp.get("radiusKm") ? Number(sp.get("radiusKm")) : 25;
  const initialLocation: LocationValue | null = hasLocation
    ? { lat: latitude!, lng: longitude!, displayName: sp.get("locationName") ?? "" }
    : null;

  // Re-search from the top search bar — updates q + location on the current per-type route,
  // preserving the active filters. (Populated with the current query, per the old page.)
  const onSearchBar = (query: string, _topicId: string | undefined, loc: LocationValue | null | undefined) => {
    const next = new URLSearchParams(sp);
    if (query) next.set("q", query); else next.delete("q");
    // Topics are subject-specific; a new query may resolve a different subject, so reset them.
    next.delete("topics");
    if (loc) {
      next.set("lat", String(loc.lat)); next.set("lng", String(loc.lng));
      if (loc.displayName) next.set("locationName", loc.displayName); else next.delete("locationName");
      if (!next.get("radiusKm")) next.set("radiusKm", "25");
    } else {
      ["lat", "lng", "locationName", "radiusKm"].forEach(k => next.delete(k));
    }
    setSp(next);
  };

  // Per-type + teachingMode + topic filters (not part of FiltersState)
  const selectedModes = sp.getAll("teachingMode");
  const selectedTopics = sp.getAll("topics");
  const batchSizeMax = sp.get("batchSizeMax") ? Number(sp.get("batchSizeMax")) : undefined;
  const sessionsPerWeek = sp.get("sessionsPerWeek") ? Number(sp.get("sessionsPerWeek")) : undefined;
  const sessionDurationMinutes = sp.get("sessionDurationMinutes") ? Number(sp.get("sessionDurationMinutes")) : undefined;
  const mockTestsIncluded = sp.get("mockTestsIncluded") === "true" ? true : undefined;

  // active count incl. extras (for the mobile trigger badge)
  const extraActive = selectedModes.length + selectedTopics.length + (batchSizeMax != null ? 1 : 0) + (sessionsPerWeek != null ? 1 : 0)
    + (sessionDurationMinutes != null ? 1 : 0) + (mockTestsIncluded ? 1 : 0);
  const baseActive = [
    filters.sortBy !== "RELEVANCE", filters.priceMin != null || filters.priceMax != null,
    filters.gradeBand.length > 0, filters.language.length > 0, filters.curriculum.length > 0,
    !!filters.availabilityTimeOfDay, filters.trialSessionAvailable === true, !!filters.targetExamId, !!filters.topicKeywords,
  ].filter(Boolean).length;
  const activeCount = baseActive + extraActive;

  // Whether the user has actually asked for anything. A param-free visit — e.g. the
  // sidebar "Find One-to-One Teachers / Group Classes / Crash Courses" links — carries
  // no query, subject, location, or filter, so it opens a clean search page instead of
  // auto-running a search over the whole catalog (which, for a logged-in student, the
  // backend ranks by their saved goal — making the page read as pre-scoped to the goal).
  const hasSearchCriteria = !!q || !!subjectId || hasLocation || activeCount > 0;

  // Temporarily remember the last search per offering type, so re-entering via the
  // bare "Find …" sidebar links restores the student's last criteria (browser Back
  // already restores via the URL). Guard on offeringType, not a boolean: this
  // component reconciles across the three /search/* routes rather than remounting,
  // so a boolean guard would skip restore on a type switch and could wipe another
  // type's memory. Restoring returns before the clear branch, so switching types
  // never forgets the type you left; clearSearch only fires when you empty filters
  // within the same type.
  const initedType = useRef<OfferingType | null>(null);
  useEffect(() => {
    const action = decideSearchMemory({
      prevType: initedType.current,
      offeringType,
      hasSearchCriteria,
      currentQuery: sp.toString(),
    });
    initedType.current = offeringType;
    if (action.kind === "restore") setSp(new URLSearchParams(action.query), { replace: true });
    else if (action.kind === "save") saveSearch(offeringType, action.query);
    else clearSearch(offeringType);
  }, [sp, hasSearchCriteria, offeringType, setSp]);

  // ── URL mutation helpers ──
  const patchParam = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null || v === "") next.delete(k); else next.set(k, v);
    });
    setSp(next);
  };
  const setMultiParam = (key: string, values: string[]) => {
    const next = new URLSearchParams(sp);
    next.delete(key);
    values.forEach(v => next.append(key, v));
    setSp(next);
  };

  // FilterPanel patch → URL (FiltersState fields)
  const handleFiltersChange = (patch: Partial<FiltersState>) => {
    const next = new URLSearchParams(sp);
    const setScalar = (k: string, v: string | number | boolean | null | undefined, dflt?: unknown) => {
      if (v == null || v === "" || v === dflt) next.delete(k); else next.set(k, String(v));
    };
    // Detects the explicit "Clear all" call (`{ ...DEFAULT_FILTERS }`) by value, not by which
    // keys happen to be present — MoreFiltersDrawer's "Apply filters" button also passes a
    // full FiltersState object (its local draft, not a single-field patch), which has the same
    // keys as DEFAULT_FILTERS but the user's actual selections. A key-presence check treated
    // every Apply click as a clear-all, silently discarding whatever the user had picked.
    const isClearAll = JSON.stringify(patch) === JSON.stringify(DEFAULT_FILTERS);
    if (isClearAll) {
      // Clear FiltersState fields + the per-type/teachingMode extras, preserve q/subjectId/location
      ["sortBy","priceMin","priceMax","gradeBand","language","curriculum","availabilityTimeOfDay",
       "trialSessionAvailable","targetExamId","topicKeywords","topics","teachingMode","batchSizeMax",
       "sessionsPerWeek","sessionDurationMinutes","mockTestsIncluded"].forEach(k => next.delete(k));
      setSp(next);
      return;
    }
    if ("sortBy" in patch) setScalar("sortBy", patch.sortBy, "RELEVANCE");
    if ("priceMin" in patch) setScalar("priceMin", patch.priceMin);
    if ("priceMax" in patch) setScalar("priceMax", patch.priceMax);
    if ("availabilityTimeOfDay" in patch) setScalar("availabilityTimeOfDay", patch.availabilityTimeOfDay);
    if ("trialSessionAvailable" in patch) setScalar("trialSessionAvailable", patch.trialSessionAvailable === true ? "true" : null);
    if ("targetExamId" in patch) setScalar("targetExamId", patch.targetExamId);
    if ("topicKeywords" in patch) setScalar("topicKeywords", patch.topicKeywords);
    if ("gradeBand" in patch) { next.delete("gradeBand"); (patch.gradeBand ?? []).forEach(v => next.append("gradeBand", v)); }
    if ("language" in patch) { next.delete("language"); (patch.language ?? []).forEach(v => next.append("language", v)); }
    if ("curriculum" in patch) { next.delete("curriculum"); (patch.curriculum ?? []).forEach(v => next.append("curriculum", v)); }
    setSp(next);
  };

  // ── Build query params ── (page is threaded through by useInfiniteQuery, not carried here)
  const common: Omit<CommonOfferingSearchParams, "page"> = useMemo(() => ({
    q, subjectId, sortBy: filters.sortBy, size: 20,
    latitude, longitude, radiusKm: hasLocation ? radiusKm : undefined,
    gradeBand: filters.gradeBand.length ? filters.gradeBand : undefined,
    language: filters.language.length ? filters.language : undefined,
    curriculum: filters.curriculum.length ? filters.curriculum : undefined,
    teachingMode: selectedModes.length ? selectedModes : undefined,
    availabilityTimeOfDay: filters.availabilityTimeOfDay || undefined,
    trialSessionAvailable: filters.trialSessionAvailable === true || undefined,
    targetExamId: filters.targetExamId || undefined,
    topicIds: selectedTopics.length ? selectedTopics : undefined,
    priceMin: filters.priceMin ?? undefined,
    priceMax: filters.priceMax ?? undefined,
    accessToken,
  }), [q, subjectId, filters, latitude, longitude, hasLocation, radiusKm, selectedModes.join(","), selectedTopics.join(","), accessToken]);

  const groupParams: Omit<GroupOfferingSearchParams, "page"> = { ...common, batchSizeMax, sessionsPerWeek, sessionDurationMinutes };
  const crashParams: Omit<CrashCourseSearchParams, "page"> = { ...common, sessionDurationMinutes, mockTestsIncluded };

  const oneToOne = useOneToOneSearchInfinite(common, offeringType === "ONE_TO_ONE" && hasSearchCriteria);
  const group = useGroupSearchInfinite(groupParams, offeringType === "GROUP" && hasSearchCriteria);
  const crash = useCrashCourseSearchInfinite(crashParams, offeringType === "CRASH_COURSE" && hasSearchCriteria);
  const query = offeringType === "ONE_TO_ONE" ? oneToOne : offeringType === "GROUP" ? group : crash;

  const firstPage = query.data?.pages[0];
  const results = query.data?.pages.flatMap(p => p.content) ?? [];
  const totalElements = firstPage?.totalElements ?? 0;

  // Topics filter: show the identified subject's configured topics as a multi-select.
  // Explicit subjectId wins; otherwise use the subject the backend resolved from the query.
  const resolvedSubjectId = subjectId ?? firstPage?.resolvedSubjectId ?? undefined;
  const { topics: subjectTopics } = useSubjectTopics(resolvedSubjectId);

  // Snapshot of the active search context passed to each card's match-score
  // breakdown so the popover totals match the ranked score (see OfferingCard).
  // Memoised for referential stability — the card re-fetches when it changes.
  // subject uses the backend-resolved query intent (a catalog name); when the
  // query didn't resolve to a subject it stays undefined and the breakdown
  // falls back to the saved goal profile — matching what the tile score does.
  const searchContext = useMemo<SearchContextSnapshot>(() => ({
    subject: firstPage?.parsedIntent?.subject ?? undefined,
    gradeBand: filters.gradeBand.length > 0 ? filters.gradeBand[0] : undefined,
    curriculum: filters.curriculum.length > 0 ? filters.curriculum[0] : undefined,
    budgetMax: filters.priceMax ?? undefined,
    examId: filters.targetExamId || undefined,
    language: filters.language.length > 0 ? filters.language[0] : undefined,
  }), [
    firstPage?.parsedIntent?.subject,
    filters.priceMax,
    filters.targetExamId,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    filters.gradeBand.join(","),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    filters.curriculum.join(","),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    filters.language.join(","),
  ]);

  // Chips for selections that live outside FiltersState (teaching mode, topics, and the
  // per-type GROUP/CRASH extras). ActiveFilterChips only knows FiltersState, so the page
  // supplies these so every applied filter appears in the top chip bar (ADR-088 design).
  const extraChips: Chip[] = [
    ...selectedModes.map(code => ({
      key: `teachingMode-${code}`,
      label: teachingModes.labelOf(code),
      onRemove: () => setMultiParam("teachingMode", selectedModes.filter(c => c !== code)),
    })),
    ...selectedTopics.map(code => ({
      key: `topic-${code}`,
      label: subjectTopics.find(t => t.code === code)?.label ?? code,
      onRemove: () => setMultiParam("topics", selectedTopics.filter(c => c !== code)),
    })),
    ...(batchSizeMax != null ? [{
      key: "batchSizeMax",
      label: `Max batch ${batchSizeMax}`,
      onRemove: () => patchParam({ batchSizeMax: null }),
    }] : []),
    ...(sessionsPerWeek != null ? [{
      key: "sessionsPerWeek",
      label: `${sessionsPerWeek} sessions/week`,
      onRemove: () => patchParam({ sessionsPerWeek: null }),
    }] : []),
    ...(mockTestsIncluded ? [{
      key: "mockTestsIncluded",
      label: "Mock tests included",
      onRemove: () => patchParam({ mockTestsIncluded: null }),
    }] : []),
  ];

  // ── Per-type extra filter sections (styled to match FilterPanel) ──
  const extraSections = (
    <>
      {teachingModes.entries.length > 0 && (
        <>
          <section>
            <SectionLabel>Teaching mode</SectionLabel>
            <MultiSelectChips
              options={teachingModes.entries}
              selected={selectedModes}
              onToggle={(code) => setMultiParam("teachingMode", selectedModes.includes(code) ? selectedModes.filter(c => c !== code) : [...selectedModes, code])}
              placeholder="Any mode"
            />
          </section>
          <Divider />
        </>
      )}
      {subjectTopics.length > 0 && (
        <>
          <section>
            <SectionLabel>Topics</SectionLabel>
            <MultiSelectChips
              options={subjectTopics}
              selected={selectedTopics}
              onToggle={(code) => setMultiParam("topics", selectedTopics.includes(code) ? selectedTopics.filter(c => c !== code) : [...selectedTopics, code])}
              placeholder="Any topic"
            />
          </section>
          <Divider />
        </>
      )}
      {offeringType === "GROUP" && (
        <>
          <section>
            <SectionLabel>Max batch size</SectionLabel>
            <Input type="number" inputMode="numeric" placeholder="e.g. 15" defaultValue={batchSizeMax ?? ""}
              onBlur={(e) => patchParam({ batchSizeMax: e.target.value })} className="h-9 text-sm w-28" aria-label="Maximum batch size" />
          </section>
          <Divider />
          <section>
            <SectionLabel>Sessions per week</SectionLabel>
            <Input type="number" inputMode="numeric" placeholder="e.g. 3" defaultValue={sessionsPerWeek ?? ""}
              onBlur={(e) => patchParam({ sessionsPerWeek: e.target.value })} className="h-9 text-sm w-28" aria-label="Sessions per week" />
          </section>
          <Divider />
        </>
      )}
      {offeringType === "CRASH_COURSE" && (
        <>
          <section className="flex items-center justify-between">
            <Label htmlFor="mock-toggle" className="text-sm font-medium cursor-pointer">Mock tests included</Label>
            <Switch id="mock-toggle" checked={!!mockTestsIncluded}
              onCheckedChange={(c) => patchParam({ mockTestsIncluded: c ? "true" : null })} className="cursor-pointer" />
          </section>
          <Divider />
        </>
      )}
    </>
  );

  // Result grid columns adapt to the available width — narrower when the filter panel
  // is open and (especially) inside the logged-in student shell; wider when collapsed.
  const gridCols = embedded
    ? (showFilters ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3")
    : (showFilters ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4");

  const { Icon: TypeIcon, badge, badgeCls, iconCls, emptyIconBg } = TYPE_CONFIG[offeringType];

  // ── Quick-start subject chips shown on the blank landing state ──
  const QUICK_SUBJECTS: Record<OfferingType, string[]> = {
    ONE_TO_ONE: ["Mathematics", "Physics", "Chemistry", "Biology", "English", "History", "Economics", "Computer Science"],
    GROUP:      ["Mathematics", "IIT-JEE", "NEET", "English", "Physics", "Chemistry", "Coding", "Economics"],
    CRASH_COURSE: ["IIT-JEE", "NEET", "Board Exams", "IELTS", "SAT", "Mathematics", "Physics", "Chemistry"],
  };

  // ── Type-specific landing copy ──
  const LANDING_COPY: Record<OfferingType, { headline: string; sub: string }> = {
    ONE_TO_ONE: {
      headline: "Find your perfect private tutor",
      sub: "Get personalised 1-on-1 lessons tailored to your pace, goals, and schedule.",
    },
    GROUP: {
      headline: "Join a class, learn together",
      sub: "Affordable group coaching with expert teachers — great for structured learning and peer motivation.",
    },
    CRASH_COURSE: {
      headline: "Intensive prep, fast results",
      sub: "Short, focused crash courses designed to get you exam-ready in the least time possible.",
    },
  };
  const landing = LANDING_COPY[offeringType];

  const body = (
    <div className={embedded ? "flex-1 px-4 sm:px-6 py-4 overflow-auto" : "container mx-auto px-4 lg:px-8 pt-44 pb-12"}>

      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-3xl font-black text-[#1D2045] mb-5">{PAGE_TITLES[offeringType]}</h1>

        {/* Session type tab switcher */}
        <div className="flex flex-wrap gap-2 mb-5">
          {TYPE_TABS.map(({ type, label, path, Icon, activeCls, hoverCls }) => {
            const isActive = type === offeringType;
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  const next = new URLSearchParams();
                  if (sp.get("q")) next.set("q", sp.get("q")!);
                  navigate(`${path}${next.toString() ? `?${next}` : ""}`);
                }}
                className={cn(
                  "inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border transition-all duration-150 cursor-pointer",
                  isActive ? activeCls : `bg-white text-[#5A5E80] border-[#DCDDEE] ${hoverCls}`,
                )}
                aria-current={isActive ? "page" : undefined}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            );
          })}
        </div>

        {/* Search bar + filter toggle side by side */}
        <div className="flex items-center gap-3 max-w-2xl">
          <div className="flex-1">
            <SearchBar
              key={q ?? ""}
              compact
              initialQuery={q ?? ""}
              initialLocation={initialLocation}
              onSearch={onSearchBar}
            />
          </div>

          {/* Desktop filter toggle — lives next to the search bar */}
          {hasSearchCriteria && (
            <button
              type="button"
              onClick={() => setShowFilters(v => !v)}
              className={cn(
                "hidden lg:inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-xl border transition-colors cursor-pointer shrink-0",
                showFilters
                  ? "bg-[#EEEFF9] text-[#262A7A] border-[#DCDDEE] hover:bg-slate-200"
                  : "bg-white text-[#5A5E80] border-[#DCDDEE] hover:bg-[#F7F7FC] hover:text-[#1D2045]",
              )}
              aria-pressed={showFilters}
            >
              {showFilters ? <PanelLeftClose className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />}
              {showFilters ? "Hide filters" : "Filters"}
              {activeCount > 0 && (
                <span className="ml-0.5 bg-indigo-600 text-white text-xs font-black w-4 h-4 rounded-full flex items-center justify-center">
                  {activeCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Mobile: filter trigger + count — only when there's something to filter */}
      {hasSearchCriteria && (
        <div className="flex lg:hidden items-center justify-between gap-4 mb-4">
          <MoreFiltersDrawer
            filters={filters}
            onFiltersChange={handleFiltersChange}
            priceMax={geoDefaults?.maxPrice}
            activeCount={activeCount}
            currency={geoDefaults?.currency}
            hasLocation={hasLocation}
            radiusKm={radiusKm}
            onRadiusChange={(km) => patchParam({ radiusKm: String(km) })}
            hideOfferingType
            sortItems={sortItems}
            extraSections={extraSections}
            hideTopicKeywords
          />
          <div className="text-sm text-muted-foreground">
            {query.isLoading ? (
              <span className="flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</span>
            ) : (
              <span><span className="font-bold text-foreground">{totalElements}</span> {NOUN[offeringType]}</span>
            )}
          </div>
        </div>
      )}

      {activeCount > 0 && (
        <div className="mb-4">
          <ActiveFilterChips filters={filters} onFiltersChange={handleFiltersChange} currency={geoDefaults?.currency} extraChips={extraChips} />
        </div>
      )}

      <div className="flex gap-8">
        {/* Desktop sidebar — collapsible to give results more room (esp. in the logged-in shell) */}
        {showFilters && hasSearchCriteria && (
          <aside className="hidden lg:block w-[280px] shrink-0">
            <div className={`sticky ${embedded ? "top-4" : "top-44"} max-h-[calc(100vh-11rem)] overflow-y-auto pr-2 pb-8`}>
              <FilterPanel
                filters={filters}
                onFiltersChange={handleFiltersChange}
                priceMax={geoDefaults?.maxPrice}
                currency={geoDefaults?.currency}
                hasLocation={hasLocation}
                radiusKm={radiusKm}
                onRadiusChange={(km) => patchParam({ radiusKm: String(km) })}
                hideOfferingType
                sortItems={sortItems}
                extraSections={extraSections}
                hideTopicKeywords
              />
            </div>
          </aside>
        )}

        {/* Results column */}
        <div className="flex-1 min-w-0">
          {/* Result count bar — only visible when results exist */}
          {hasSearchCriteria && (
            <div className="hidden lg:flex items-center justify-between mb-5 pb-4 border-b border-[#DCDDEE]">
              <div className="text-sm text-[#5A5E80]">
                {query.isLoading ? (
                  <span className="flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</span>
                ) : (
                  <>
                    <span className="font-bold text-[#1D2045]">{totalElements}</span> {NOUN[offeringType]} found
                    {q && (<> for <span className="font-semibold text-[#262A7A]">"{q}"</span></>)}
                  </>
                )}
              </div>
            </div>
          )}

          {!hasSearchCriteria ? (
            /* ── Rich blank landing state ── */
            <div className="mt-4 sm:mt-8">
              {/* Hero card */}
              <div className={cn(
                "rounded-2xl border border-[#DCDDEE] bg-white shadow-sm px-8 py-10 text-center mb-8",
              )}>
                <div className={cn("w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-sm", emptyIconBg)}>
                  <TypeIcon className={cn("w-7 h-7", iconCls)} />
                </div>
                <h2 className="text-xl font-bold text-[#1D2045] mb-2">{landing.headline}</h2>
                <p className="text-sm text-[#5A5E80] max-w-md mx-auto leading-relaxed">{landing.sub}</p>
              </div>

              {/* Popular subjects */}
              <div>
                <p className="text-xs font-semibold text-[#A9ACD6] uppercase tracking-wider mb-3">Popular subjects</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_SUBJECTS[offeringType].map((subject) => (
                    <button
                      key={subject}
                      type="button"
                      onClick={() => {
                        const next = new URLSearchParams(sp);
                        next.set("q", subject);
                        setSp(next);
                      }}
                      className={cn(
                        "inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-[#DCDDEE] bg-white text-[#262A7A]",
                        "hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 transition-colors cursor-pointer",
                      )}
                    >
                      <Search className="w-3.5 h-3.5 opacity-50" />
                      {subject}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : query.isLoading ? (
            <div className={`grid ${gridCols} gap-5`}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl overflow-hidden bg-white shadow-md">
                  <div className="h-36 w-full bg-[#EEEFF9] animate-pulse" />
                  <div className="p-4 space-y-2.5">
                    <div className="h-5 w-3/4 bg-[#EEEFF9] rounded-lg animate-pulse" />
                    <div className="h-4 w-1/2 bg-[#EEEFF9] rounded-lg animate-pulse" />
                    <div className="h-4 w-2/3 bg-[#EEEFF9] rounded-lg animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : query.isError ? (
            <EmptyState
              icon={Search}
              title="Something went wrong"
              description="Please try again."
              iconBgClassName="bg-red-50"
              iconClassName="text-red-400"
              action={<Button onClick={() => query.refetch()} variant="outline" className="rounded-full px-6 cursor-pointer">Retry</Button>}
            />
          ) : results.length === 0 ? (
            <ZeroResultsState
              filters={Object.fromEntries(sp.entries())}
              hasActiveFilters={activeCount > 0 || !!q || !!subjectId}
              onClearFilters={() => handleFiltersChange({ ...DEFAULT_FILTERS })}
              accessToken={accessToken}
              offeringType={offeringType}
            />
          ) : (
            <>
              <div className={`grid ${gridCols} gap-5`}>
                {results.map(o => (
                  <OfferingCard key={o.offeringId} offering={o} searchContext={searchContext} showMatchBreakdown activeGradeBands={filters.gradeBand} />
                ))}
              </div>
              {query.hasNextPage && (
                <div className="flex justify-center mt-10">
                  <Button
                    variant="outline"
                    onClick={() => query.fetchNextPage()}
                    disabled={query.isFetchingNextPage}
                    className="cursor-pointer rounded-full px-8 py-2.5 border-[#DCDDEE] text-[#5A5E80] hover:bg-[#F7F7FC] font-semibold"
                  >
                    {query.isFetchingNextPage ? (
                      <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</span>
                    ) : (
                      "Load more results"
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  const SEO_META: Record<string, { title: string; description: string; path: string }> = {
    ONE_TO_ONE: {
      title: 'One-to-One Private Tutors | BookMyCoaching',
      description: 'Find verified private tutors for CBSE, IGCSE, IIT-JEE and more. Filter by subject, grade, and location.',
      path: '/search/one-to-one',
    },
    GROUP: {
      title: 'Group Classes & Coaching | BookMyCoaching',
      description: 'Browse group coaching classes for competitive exams, school subjects, and skill development.',
      path: '/search/group',
    },
    CRASH_COURSE: {
      title: 'Crash Courses | BookMyCoaching',
      description: 'Short, intensive crash courses for board exams, entrance tests, and competitive exams.',
      path: '/search/crash-course',
    },
  };
  const seoMeta = SEO_META[offeringType] ?? SEO_META.ONE_TO_ONE;
  const searchHelmet = (
    <Helmet>
      <title>{seoMeta.title}</title>
      <meta name="description" content={seoMeta.description} />
      <link rel="canonical" href={getCanonicalUrl(seoMeta.path)} />
      <meta property="og:title" content={seoMeta.title} />
      <meta property="og:description" content={seoMeta.description} />
      <meta property="og:url" content={getCanonicalUrl(seoMeta.path)} />
      <meta property="og:image" content={getDefaultOgImage()} />
    </Helmet>
  );

  if (embedded) return <div className="flex flex-col min-h-full">{searchHelmet}{body}</div>;
  return (
    <div className="min-h-screen bg-background">
      {searchHelmet}
      <Header />
      {body}
      <Footer />
    </div>
  );
}
